import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { FileText, Trash2, ExternalLink, Calendar, User, MapPin, FolderOpen, Sparkles, Pencil, X, Send } from 'lucide-react';
import { toast } from 'sonner';
import ItineraryBuilder from './ItineraryBuilder';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? d : dt.toLocaleDateString('en-US', { dateStyle: 'medium' });
};

const AdminSavedItineraries = () => {
  const { adminAuth } = useOutletContext();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inquiries, setInquiries] = useState([]);
  const [pickerFor, setPickerFor] = useState(null);
  const [selectedInquiryId, setSelectedInquiryId] = useState('');
  const [builder, setBuilder] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/saved-itineraries`, {
        headers: { Authorization: `Basic ${adminAuth}` },
      });
      if (res.ok) setItems(await res.json());
    } catch {
      toast.error('Failed to load saved itineraries');
    } finally {
      setLoading(false);
    }
  }, [adminAuth]);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm('Delete this saved itinerary and its PDF?')) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/saved-itineraries/${id}`, {
        method: 'DELETE', headers: { Authorization: `Basic ${adminAuth}` },
      });
      if (res.ok) { toast.success('Deleted'); load(); }
      else toast.error('Failed to delete');
    } catch { toast.error('Connection error'); }
  };

  const openPicker = async (item) => {
    setPickerFor(item);
    setSelectedInquiryId('');
    if (inquiries.length === 0) {
      try {
        const res = await fetch(`${API_URL}/api/admin/inquiries`, { headers: { Authorization: `Basic ${adminAuth}` } });
        if (res.ok) setInquiries(await res.json());
      } catch { toast.error('Failed to load inquiries'); }
    }
  };

  const startEditing = () => {
    const inq = inquiries.find((i) => i.id === selectedInquiryId);
    if (!inq) { toast.error('Select an inquiry first'); return; }
    setBuilder({ savedId: pickerFor.id, inquiry: inq });
    setPickerFor(null);
  };

  if (loading) return <div className="flex items-center justify-center h-64">Loading...</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Saved Itineraries</h1>
        <p className="text-gray-600">Every itinerary PDF you send or that customers generate via Instant Itinerary is saved here. Use &quot;Edit &amp; Send&quot; to change dates, hotels, places or flights and send it to any inquiry.</p>
      </div>

      {items.length === 0 ? (
        <Card className="border-dashed border-2">
          <CardContent className="p-12 text-center">
            <FolderOpen className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No saved itineraries yet</h3>
            <p className="text-gray-500">When you generate &amp; send an itinerary PDF from an inquiry, it will appear here for reuse.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((it) => (
            <Card key={it.id} data-testid={`saved-itinerary-${it.id}`} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4 flex items-center gap-4">
                <div className={`w-12 h-12 ${it.source === 'instant_itinerary' ? 'bg-purple-100' : 'bg-amber-100'} rounded-lg flex items-center justify-center shrink-0`}>
                  {it.source === 'instant_itinerary' ? (
                    <Sparkles className="w-6 h-6 text-purple-600" />
                  ) : (
                    <FileText className="w-6 h-6 text-amber-600" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-gray-900 truncate">{it.title}</h3>
                    {it.source === 'instant_itinerary' && (
                      <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded-full whitespace-nowrap">
                        AI Generated
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-4 text-xs text-gray-500 mt-1">
                    {it.client_name && <span className="flex items-center gap-1"><User className="w-3 h-3" /> {it.client_name}</span>}
                    {it.destination && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {it.destination}</span>}
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {fmt(it.created_at)}</span>
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => openPicker(it)} data-testid={`edit-send-saved-${it.id}`}
                    className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-amber-600 text-white text-sm hover:bg-amber-700">
                    <Pencil className="w-4 h-4" /> Edit &amp; Send
                  </button>
                  <a href={`${API_URL}${it.pdf_url}`} target="_blank" rel="noopener noreferrer"
                    data-testid={`view-saved-pdf-${it.id}`}
                    className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-gray-100 text-gray-700 text-sm hover:bg-gray-200">
                    <ExternalLink className="w-4 h-4" /> View PDF
                  </a>
                  <button onClick={() => remove(it.id)} data-testid={`delete-saved-${it.id}`} className="p-2 text-red-500 hover:bg-red-50 rounded">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Inquiry picker modal */}
      {pickerFor && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" data-testid="inquiry-picker-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <Send className="w-5 h-5 text-amber-600" /> Send to which inquiry?
              </h2>
              <button onClick={() => setPickerFor(null)} className="p-2 text-gray-500 hover:bg-gray-100 rounded" data-testid="close-picker-btn">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-gray-600">
                Editing <span className="font-semibold">{pickerFor.title}</span>. Choose the inquiry (client) to send the updated itinerary to — you can change dates, hotels, places and flights on the next screen.
              </p>
              <select
                value={selectedInquiryId}
                onChange={(e) => setSelectedInquiryId(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
                data-testid="inquiry-picker-select"
              >
                <option value="">Choose an inquiry…</option>
                {inquiries.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.name} — {i.destination || i.inquiry_type || 'inquiry'} ({i.email})
                  </option>
                ))}
              </select>
              {inquiries.length === 0 && <p className="text-xs text-gray-400">Loading inquiries…</p>}
            </div>
            <div className="border-t p-4 flex justify-end gap-3">
              <Button variant="outline" onClick={() => setPickerFor(null)}>Cancel</Button>
              <Button onClick={startEditing} disabled={!selectedInquiryId} className="bg-amber-600 hover:bg-amber-700" data-testid="continue-edit-btn">
                <Pencil className="w-4 h-4 mr-2" /> Continue to Editor
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Full editor */}
      {builder && (
        <ItineraryBuilder
          inquiryId={builder.inquiry.id}
          inquiry={builder.inquiry}
          adminAuth={adminAuth}
          initialSavedId={builder.savedId}
          onClose={() => setBuilder(null)}
          onSent={() => { setBuilder(null); load(); toast.success('Updated itinerary sent to the inquiry'); }}
        />
      )}
    </div>
  );
};

export default AdminSavedItineraries;
